
import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, ElementRef, forwardRef, Injector, Input, OnDestroy, Renderer2,
  TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { WizFocusManager } from '../../../../../services/core/focus/wiz-focus-manager';
import { BASE_COMPONENT, WizBaseComponent } from '../../../../core/wiz-base-component';
import { WizTextarea } from '../../wiz-text-area/wiz-text-area';
import { WizTranslationBasicPop } from './wiz-translation.basic.pop';
import { WizTextInput } from '../../wiz-text-input/wiz-text-input';
import { TRANSLATION_MODE } from './translation.config';
import { WizTranslationData } from '../wiz-translation-data.obj';
import { WizDatasource } from '../../../../../services/collections/wiz-datasource';
import { WizComponentUtils } from '../../../../../utils/wiz-component.utils';
import { WizValidationErrors, WizValidatorFn } from '../../../../../services/validators/wiz-validators';
import { WizRoot } from '../../../../../components/views/wiz-root';
import { IWizTranslationEntry, VALUE_DOWN, VALUE_UP, WIZ_EVENT_TYPE, WizStringUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../../events/wiz-event';
import { WizEventManager } from '../../../../../events/wiz-event.manager';
import { IWIZ_COMPONENT} from '../../../../../interfaces/iwiz-component';
import { WizTranslationManager } from '../../../../../services/translation/wiz-translation.manager';

/**
 * Composant de saisie de libellé traductible
 * Author : MDI
 */
@Component({
  selector: 'wiz-translation-basic',
  templateUrl: './wiz-translation.basic.html',
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizTranslationBasic) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizTranslationBasic) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizTranslationBasic) }
  ]
})
export class WizTranslationBasic extends WizBaseComponent implements AfterViewInit, OnDestroy {

  @ViewChild('tiTemplate', {read: TemplateRef, static: true}) tiTemplate: TemplateRef<any>;
  @ViewChild('taTemplate', {read: TemplateRef, static: true}) taTemplate: TemplateRef<any>;

  /** le composant input principal */
  @ViewChild('mainInput') mainInput: WizTextarea | WizTextInput;

  // type de champ de saisie
  protected _wizTranslationMode: TRANSLATION_MODE = 'textinput';
  @Input()
  public set wizTranslationMode(mode: TRANSLATION_MODE) {
    this._wizTranslationMode = mode;
  }
  public get wizTranslationMode(): TRANSLATION_MODE {
    return this._wizTranslationMode;
  }

  /**
   * Accés au template à afficher en fonction du mode choisi
   */
  public get template(): TemplateRef<any> {
    switch (this.wizTranslationMode) {
      case 'textinput':
        return this.tiTemplate;
      case 'textarea':
        return this.taTemplate;
    }
  }

  /** Flag pop ouverte */
  protected _popOpened: boolean;

  /** Traduction principale (dans la langue du user) */
  protected _mainEntry: IWizTranslationEntry;

  /** Traduction par défaut (celle de la bu) */
  protected _defaultEntry: IWizTranslationEntry;

  /** Code langue par défaut */
  protected _mainLanguageCode: string;

  /** Données de traduction */
  protected _translationData: WizTranslationData;

  public get translationData(): WizTranslationData {
    return this.getTranslationData();
  }
  @Input()
  public set translationData(data: WizTranslationData) {
    this.setTranslationData(data);
  }
  public getTranslationData(): WizTranslationData {
    return this._translationData;
  }
  public setTranslationData(data: WizTranslationData) {
      this._translationData = data;
      if (data) {
        // La data source
        this.entryDS = data.entryDS;
        // La langue par défaut
        this._mainLanguageCode = data.mainLanguageCode;
        if (this._entryDS) {
          // Détermination de la trad principale
          this.entryDS.getSource().forEach(entry => {
            if (entry.languageCode === this._translationData.mainLanguageCode) {
              this._mainEntry = entry;
            }
            if (entry.languageCode === this._translationData.defaultLanguageCode) {
              this._defaultEntry = entry;
            }
          });
          // Application du filtre (exclusion de la langue principale) et du tri (langue par défaut en 1er)
          this._entryDS.addFilter(this.translationFilter.bind(this))
            .setSortFields([{ property: 'sortField', numeric: false, way: 'asc' }])
            .build();
        }
      }
      // Alimentation de l'input principal
      this.feedMainTranslation();
  }

  /** Data source d'entrées de traduction */
  protected _entryDS: WizDatasource<IWizTranslationEntry>;
  public get entryDS(): WizDatasource<IWizTranslationEntry> {
    return this.getEntryDS();
  }
  public set entryDS(ds: WizDatasource<IWizTranslationEntry>) {
    this.setEntryDS(ds);
  }
  public getEntryDS(): WizDatasource<IWizTranslationEntry> {
    return this._entryDS ? this._entryDS : new WizDatasource<IWizTranslationEntry>();
  }
  public setEntryDS(ds: WizDatasource<IWizTranslationEntry>): void {
    this._entryDS = ds;
  }

  /** Placeholder */
  protected _wizPlaceholder: string;
  @Input() public set wizPlaceholder(str: string) {
    this.setWizPlaceholder(str);
  }
  public get wizPlaceholder(): string {
    return this.getWizPlaceholder();
  }
  /**
   * methodes getter/setter surchargeables du _wizShowErrors
   */
  protected setWizPlaceholder(str: string) {
    this._wizPlaceholder = str;
  }
  protected getWizPlaceholder(): string {
    return this._wizPlaceholder;
  }

  /** Nombre max de caractères */
  protected _wizMaxChars: number;
  @Input()
  public set wizMaxChars(v: number) {
    this.setWizMaxChars(v);
  }
  public get wizMaxChars(): number {
    return this.getWizMaxChars();
  }
  /**
   * Méthodes getter/setter surchargeables du _wizMaxChars
   */
  protected setWizMaxChars(v: number) {
    this._wizMaxChars = v;
  }
  protected getWizMaxChars(): number {
    return this._wizMaxChars;
  }

  /** Affichage des erreurs */
  protected _wizShowErrors: boolean = false; // afficher les erreurs sous le bouton
  @Input()
  public set wizShowErrors(v: boolean) {
    this.setWizShowErrors(v);
  }
  public get wizShowErrors(): boolean {
    return this.getWizShowErrors();
  }
  /**
   * methodes getter/setter surchargeables du _wizShowErrors
   */
  protected setWizShowErrors(v: boolean) {
    this._wizShowErrors = v;
    this.cdref.markForCheck();
  }
  protected getWizShowErrors(): boolean {
    return this._wizShowErrors;
  }

  /** wizValue: celui de l'input principal (pour validation) */
  public getWizValue(): any {
    return this.mainInput.getWizValue();
  }

  // affichage ou non du label
  protected _wizShowLabel: boolean = true;
  @Input()
  public set wizShowLabel(v: boolean) {
    this._wizShowLabel = v;
    this.cdref.markForCheck();
  }
  public get wizShowLabel(): boolean {
    return this._wizShowLabel;
  }

  /** Nombre max de drapeau affichés dans la pop */
  private _maxTranslation: number = 5;
  @Input()
  public set maxTranslation(maxT: number) {
    this.setMaxTranslation(maxT);
    this.cdref.markForCheck();
  }
  public get maxTranslation(): number {
    return this.getMaxTranslation();
  }
  public setMaxTranslation(maxT: number) {
    this._maxTranslation = maxT;
  }

  public getMaxTranslation(): number {
    return this._maxTranslation;
  }


 /**
  * Constructeur
  * @param cdref pour surcharge du constructeur WizBasComponent
  * @param elementRef pour dispatch d'évènements
  * @param renderer pour surcharge du constructeur WizBasComponent
  * @param overlay pour gestion de la pop up
  * @param _injector pour gestion de la pop up
  */
  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef,
    public renderer: Renderer2, public overlay: Overlay, protected _injector: Injector) {
    super(cdref, elementRef, renderer);
    this.basicComponent = false;
  }

  /**
   * Pose d'écouteurs
   */
  public ngAfterViewInit(): void {
    // Changement dans l'input
    WizEventManager.addListener(this, this.mainInput, WIZ_EVENT_TYPE.CHANGE, this.onMainLabelChange);
    // key down pour les touches hors saisie > fermeture/ouverture avec les flèches
    WizEventManager.addListener(this, this.mainInput.elementRef, 'keydown', this.onKeyDown);
    // Click sur l'icone flag du text input pour ouverture de la pop
    WizEventManager.addListener(this, this.mainInput, WIZ_EVENT_TYPE.INPUT_RIGHT_CLICK, this.showPop);
    // écoute du changement de langue
    WizEventManager.addListener(this, WizRoot.getInstance().elementRef, WIZ_EVENT_TYPE.TRANSLATION_LOADED, this.translationLoaded);
    this.feedMainTranslation();
  }

  /**
   * mise à jour de la donnée traduite
   * @param e, wizevent
   */
  public translationLoaded(e: WizEvent): void {
    if (this._translationData) {
      this._translationData.mainLanguageCode = WizTranslationManager.getInstance().dateLocale;
      // TODO améliorer la façon de mettre à jour
      this.setTranslationData(this._translationData);
    }
  }

  /**
   * Release d'écouteurs
   */
  public ngOnDestroy(): void {
    if (!!this.mainInput) { // sinon, c'est que le compo a été détruit avant d'être instancié
      WizEventManager.removeListener(this, this.mainInput, WIZ_EVENT_TYPE.CHANGE);
      WizEventManager.removeListener(this, this.mainInput.elementRef, 'keydown');
      WizEventManager.removeListener(this, this.mainInput, WIZ_EVENT_TYPE.INPUT_RIGHT_CLICK);
      WizEventManager.removeListener(this, WizRoot.getInstance().elementRef, WIZ_EVENT_TYPE.TRANSLATION_LOADED);
    }
  }

  /**
   * Alimentation de la trad principale
   */
  protected feedMainTranslation(): void {
    if (this.mainInput) {
      if (this._mainEntry) {
        this.mainInput.wizValue = this._mainEntry.label;
      }
    }
  }

  /**
   * Ecouteur sur key down pour ouverture du panneau time picker
   * @param event Evènement key down
   */
  protected onKeyDown(event: KeyboardEvent): void {
    // ouverture sur up / down
    if (event.key === VALUE_DOWN || event.key === VALUE_UP) {
      if (!this._popOpened) {
        this.showPop(null);
      }
    }
  }

  /**
   * Ouverture de la pop
   */
  public showPop(e: WizEvent): void {
    if (!this._popOpened) {
      const overlayRef: OverlayRef = this.overlay.create(this.getOverlayConfig());
      overlayRef.overlayElement.setAttribute('role', 'dialog');
      const injector = Injector.create({
        providers: [{ provide: ChangeDetectorRef, useValue: this.cdref },
        { provide: OverlayRef, useValue: overlayRef }],
        parent: this._injector
      });
      const translationPortal = new ComponentPortal(WizTranslationBasicPop, null, injector);

      // Attach ComponentPortal to PortalHost
      const popRef: ComponentRef<WizTranslationBasicPop> = overlayRef.attach(translationPortal);
      // Passage des données utiles à la pop
      popRef.instance.maxTranslation = this.maxTranslation;
      popRef.instance.entryDS = this.entryDS;
      popRef.instance.parentComponent = this;
      popRef.instance.inputMode = this.wizTranslationMode;
      // popRef.instance.wizMaxChars = this.wizMaxChars;
      // Déclaration de l'affiliation au focusmanager
      WizFocusManager.getInstance().wizRegisterOwner(this.mainInput, popRef.instance.elementRef);
      // subscription au click du backdrop pour fermer la pop
      overlayRef.backdropClick().subscribe(_ => overlayRef.dispose());
      overlayRef.detachments().subscribe(_ => this.onPopClose());

      this._popOpened = true;
      // Focus du champ de saisie de la pop
      popRef.instance.wizFocus();
    }
  }

  /**
   * Configuration de l'overlay de la pop
   */
  protected getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'wiz-popup-overlay',
      panelClass: ['wiz-popup-panel', 'translation-compo-pop'],
      width: this.mainInput.elementRef.nativeElement.offsetWidth,
      // l'overlay va prendre sa position en fonction du text input
      positionStrategy: this.getOverlayPosition(this.mainInput.elementRef.nativeElement), // TODO Tout pourri sur height custom
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });
  }

  /**
   * Configuration de la position de l'overlay du time picker
   * @param origin Point d'ancrage de l'overlay
   */
  protected getOverlayPosition(origin: ElementRef): PositionStrategy {
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions(WizComponentUtils.getPreferredPositions())
      .withPush(false);
    return positionStrategy;
  }

  /**
   * reprise du focus à la fermeture du time picker
   */
  protected onPopClose(): void {
    this._popOpened = false;
    this.mainInput.wizFocus();
    this.wizGetFocus();
  }

  /**
   * Override pour focus de l'input principal
   */
  public wizGetFocus(): void {
    this.mainInput.wizGetFocus();
  }

  /**
   * Mise à jour du label principal
   * @param e Evenement change
   */
  protected onMainLabelChange(e: WizEvent): void {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this._mainEntry) {
      this._mainEntry.label = e.value;
    }
    if (this.manageModel) {
      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, this, this._mainEntry));
    }
  }

  /**
   * initialisation des méthodes de validation
   */
  public getValidators() {
    const validators: WizValidatorFn[] = this.mainInput.getValidators();
    return validators;
  }

  /**
   * Appel externe sur focusOut depuis la directive wizValidator
   * @param pErrors la collection d'erreur
   * @param wizShowErrors : //TODO JRO demande externe ? sert à rien ici
   */
  public onWizValidation(pErrors: WizValidationErrors, wizShowErrors: boolean): void {
    this.mainInput.onWizValidation(pErrors, wizShowErrors);
  }

  /** Error message container : contrat d'interface pour la directive wizValidator */
  public getErrorVCRef(): ViewContainerRef {
    return this.mainInput.getErrorVCRef();
  }

  /**
   * Validation: Renseigner le default language si non renseigné
   */
  public validateDefaultLanguage(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      const result: WizValidationErrors = [];
      if (this._mainEntry && this._defaultEntry && WizStringUtils.isEmpty(this._defaultEntry.label) &&
        !WizStringUtils.isEmpty(this._mainEntry.label)) {
        this._defaultEntry.label = this._mainEntry.label;
        WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, this, this._defaultEntry));
      }
      return result;
    };
  }

  /**
   * Filtre de base de la datasource des entrée : pas la langue principale
   * @param entry Entrée de traduction testée
   */
  protected translationFilter(entry: IWizTranslationEntry): boolean {
    return entry && entry.languageCode !== this._mainLanguageCode;
  }
}
